import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import OvertimeHeader from '../../components/hris/overtime/OvertimeHeader';
import OvertimeStats from '../../components/hris/overtime/OvertimeStats';
import OvertimeTable from '../../components/hris/overtime/OvertimeTable';
import OvertimeModal from '../../components/hris/overtime/OvertimeModal';
import DeleteOvertimeDialog from '../../components/hris/overtime/DeleteOvertimeDialog';
import { useToast } from '../../context/ToastContext';
import { containerVariants, itemVariants } from '../../utils/animations';

export default function OvertimeList() {
  const { showSuccess, showError } = useToast();
  const [overtimes, setOvertimes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAdmin] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOvertime, setEditingOvertime] = useState(null);
  const [deleteOvertimeId, setDeleteOvertimeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [overtimeForm, setOvertimeForm] = useState({
    employee_id: '',
    date: '',
    start_time: '17:00',
    end_time: '20:00',
    duration_hours: '3',
    rate_per_hour: '50000',
    total_amount: '150000',
    description: '',
    status: 'pending',
  });

  const loadData = useCallback(async () => {
    try {
      const response = await apiClient.get(
        `/hris/overtime?month=${selectedMonth}&year=${selectedYear}`
      );
      setOvertimes(response.overtimes || []);

      // Load employees
      const empResponse = await apiClient.get('/hris/employees');
      setEmployees(empResponse.employees || []);
    } catch (error) {
      // Error loading data
      setOvertimes([]);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDialog = (overtime = null, empId = null) => {
    if (overtime) {
      setEditingOvertime(overtime);
      setOvertimeForm({
        employee_id: overtime.employee_id,
        date: overtime.date,
        start_time: overtime.start_time || '17:00',
        end_time: overtime.end_time || '20:00',
        duration_hours: String(overtime.duration_hours || 3),
        rate_per_hour: String(overtime.rate_per_hour || 50000),
        total_amount: String(overtime.total_amount || 150000),
        description: overtime.description || '',
        status: overtime.status || 'pending',
      });
    } else {
      setEditingOvertime(null);
      setOvertimeForm({
        employee_id: empId || '',
        date: new Date().toISOString().split('T')[0],
        start_time: '17:00',
        end_time: '20:00',
        duration_hours: '3',
        rate_per_hour: '50000',
        total_amount: '150000',
        description: '',
        status: 'pending',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!overtimeForm.employee_id || !overtimeForm.date) {
      showError('Harap isi pegawai dan tanggal');
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        ...overtimeForm,
        duration_hours: parseFloat(overtimeForm.duration_hours),
        rate_per_hour: parseFloat(overtimeForm.rate_per_hour),
        total_amount: parseFloat(overtimeForm.total_amount),
      };
      if (editingOvertime) {
        await apiClient.put(`/hris/overtime/${editingOvertime.id}`, dataToSave);
        showSuccess('Data lembur berhasil diperbarui');
      } else {
        await apiClient.post('/hris/overtime', dataToSave);
        showSuccess('Data lembur berhasil ditambahkan');
      }
      loadData();
      setDialogOpen(false);
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan data lembur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteOvertimeId) return;
    try {
      await apiClient.delete(`/hris/overtime/${deleteOvertimeId}`);
      showSuccess('Data lembur berhasil dihapus');
      loadData();
      setDeleteOvertimeId(null);
    } catch (error: any) {
      showError(error?.message || 'Gagal menghapus data lembur');
    }
  };

  const handleStatusChange = async (overtime, newStatus) => {
    try {
      await apiClient.put(`/hris/overtime/${overtime.id}`, {
        ...overtime,
        status: newStatus,
      });
      const statusLabel =
        newStatus === 'approved'
          ? 'disetujui'
          : newStatus === 'rejected'
            ? 'ditolak'
            : 'diperbarui';
      showSuccess(`Status lembur berhasil ${statusLabel}`);
      loadData();
    } catch (error: any) {
      showError(error?.message || 'Gagal memperbarui status lembur');
    }
  };

  const stats = {
    total: overtimes.length,
    pending: overtimes.filter(o => o.status === 'pending').length,
    approved: overtimes.filter(o => o.status === 'approved').length,
    rejected: overtimes.filter(o => o.status === 'rejected').length,
    totalHours: overtimes.reduce((sum, o) => sum + (o.duration_hours || 0), 0),
    totalAmount: overtimes.reduce((sum, o) => sum + (o.total_amount || 0), 0),
  };

  const filteredOvertimes = overtimes.filter(ot => {
    if (filterStatus === 'all') return true;
    return ot.status === filterStatus;
  });

  const getEmployeeName = empId => employees.find(e => e.id === empId)?.full_name || '-';
  const getEmployeeId = empId => employees.find(e => e.id === empId)?.employee_id || '-';

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <OvertimeHeader
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          isAdmin={isAdmin}
          onAdd={() => openDialog()}
        />
      </motion.div>
      <motion.div variants={itemVariants}>
        <OvertimeStats stats={stats} />
      </motion.div>
      <motion.div variants={itemVariants}>
        <OvertimeTable
          overtimes={filteredOvertimes}
          loading={loading}
          isAdmin={isAdmin}
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          getEmployeeName={getEmployeeName}
          getEmployeeId={getEmployeeId}
          onEdit={ot => openDialog(ot)}
          onDelete={id => setDeleteOvertimeId(id)}
          onAdd={empId => openDialog(null, empId)}
          onStatusChange={handleStatusChange}
        />
      </motion.div>
      <OvertimeModal
        open={dialogOpen}
        editing={editingOvertime}
        form={overtimeForm}
        setForm={setOvertimeForm}
        employees={employees}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
      <DeleteOvertimeDialog
        show={!!deleteOvertimeId}
        onClose={() => setDeleteOvertimeId(null)}
        onConfirm={handleDelete}
      />
    </motion.div>
  );
}
